import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Divider, Loader, Stack, Title } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/_Input";
import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSearchCustomer } from "@/features/dashboard/pedidos/_hooks/useSearchCustomer";
import { useSearchDrivers } from "@/features/dashboard/pedidos/_hooks/useSearchDrivers";
import { useSearchOrder } from "@/features/dashboard/pedidos/_hooks/useSearchOrders";
import { useSearchTrucks } from "@/features/dashboard/pedidos/_hooks/useSearchTrucks";
import { useIsMobile } from "@/hooks";
import { Role, useGetTruckShippingFiltersQuery, useLazyGetSchedulingsQuery } from "@/store";
import { Scheduling } from "@/store/apis/nextcargo/scheduling";
import { transformInOptions } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_trucks/caminhoes/carregamentos/")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Histórico de Carregamentos" }] }),
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

type Schema = z.infer<typeof schema>;
const schema = z.object({
  dateRange: z.tuple([z.string(), z.string().optional()]),
  groupBy: z.string().optional().nullable(),
  ids: z.object({
    company: z.string().optional().nullable(),
    driver: z.string().optional().nullable(),
    truck: z.string().optional().nullable(),
    order: z.string().optional().nullable(),
    customer: z.string().optional().nullable()
  })
});
function RouteComponent() {
  const isMobile = useIsMobile();

  // const [_queryState] = useQueryState("json", parseAsJson(orderSearchParamsSchema));
  const [getSchedules, { data = [], isFetching }] = useLazyGetSchedulingsQuery();
  const {
    data: filtersData,
    isLoading: filtersLoading,
    refetch: refetchFilters
  } = useGetTruckShippingFiltersQuery(undefined, {
    selectFromResult: (state) => ({
      ...state,
      data: {
        companies: transformInOptions(state.data?.companies),
        orders: transformInOptions(state.data?.orders, { getLabel: (item) => item.code ?? "N/A" })
      }
    })
  });
  const {
    data: searchTrucksResult,
    actions: { setSearchTruck },
    state: { searchTruck, isFetching: isFetchingTruck }
  } = useSearchTrucks();

  const {
    data: searchDriversResult,
    actions: { setSearchDriver },
    state: { searchDriver, isFetching: isFetchingDriver }
  } = useSearchDrivers();

  const {
    data: searchCustomersResult,
    actions: { setSearchCustomer },
    state: { searchCustomer, isFetching: isFetchingCustomer }
  } = useSearchCustomer();

  const {
    data: searchOrdersResult,
    actions: { setSearchOrder },
    state: { searchOrder, isFetching: isFetchingOrder }
  } = useSearchOrder();

  const groupByOptions = [
    { value: "customer", label: "Cliente" },
    { value: "order", label: "Pedido" },
    { value: "truck", label: "Caminhão" },
    { value: "driver", label: "Motorista" }
  ];
  const dateFormat = "YYYY-MM-DD";
  const today = dayjs();

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      dateRange: [today.startOf("month").format(dateFormat), today.format(dateFormat)]
    }
  });
  const { dateRange, ...values } = getValues();
  const [startDate, endDate] = dateRange;

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const {
      dateRange: [startDate, endDate],
      groupBy,
      ids
    } = data;

    await getSchedules({
      startDate: dayjs(startDate, dateFormat).toISOString(),
      endDate: dayjs(endDate, dateFormat).toISOString(),
      groupById: groupBy,
      ids
    });
  };

  const columns: GridColDef<Scheduling>[] = [
    { field: "id", headerName: "ID", width: 100, align: "center", headerAlign: "center", type: "number" },
    {
      field: "order",
      headerName: "Pedido",
      valueGetter: (val, { order }) => order.code,
      type: "string",
      minWidth: 125,
      renderCell: ({ value }) => <p>Pedido {value || "N/A"}</p>
    },
    { field: "createdAt", headerName: "Data", minWidth: 150, valueFormatter: (val) => dayjs(val).format("DD/MM/YYYY, HH:mm"), type: "string" },
    { field: "customer", headerName: "Cliente", valueGetter: (val, { order }) => order?.customer?.name, flex: 1, minWidth: 200, type: "string" },
    { field: "company", headerName: "Transportadora", width: 150, flex: 1, minWidth: 200, valueGetter: (val, { company }) => company.name, type: "string" },
    { field: "driver", headerName: "Motorista", flex: 1, minWidth: 200, valueGetter: (val, { driver }) => driver.name, type: "string" },
    { field: "truckPlate", headerName: "Caminhão", width: 150, valueGetter: (val, { truck }) => truck.licensePlate, type: "string" },
    { field: "qtyTransported", headerName: "Qtd. Transportada", width: 150, type: "number", valueGetter: (val) => `${val}t` }
  ];

  const datePresets = useMemo(
    () => [
      {
        label: "Últimos 7 dias",
        value: [today.subtract(7, "day").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Últimos 30 dias",
        value: [today.subtract(30, "day").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Últimos 60 dias",
        value: [today.subtract(60, "day").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Últimos 90 dias",
        value: [today.subtract(90, "day").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Últimos 120 dias",
        value: [today.subtract(120, "day").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Último ano",
        value: [today.subtract(1, "year").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Hoje",
        value: [today.format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Este mês",
        value: [today.startOf("month").format(dateFormat), today.format(dateFormat)]
      },
      {
        label: "Mês passado",
        value: [today.subtract(1, "month").startOf("month").format(dateFormat), today.subtract(1, "month").endOf("month").format(dateFormat)]
      }
    ],
    []
  );

  const refetch = () => {
    refetchFilters();
    getSchedules({ startDate, endDate, ...values });
  };

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <DashboardComponents.Title>Listagem de Carregamentos</DashboardComponents.Title>

        <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Carregamentos" }} onClick={refetch} loading={isFetching} />
      </div>

      <Card component="form" onSubmit={handleSubmit(onSubmit)} mb="lg" p={isMobile ? "xs" : "md"} withBorder>
        <Stack>
          <Title order={3}>Filtros</Title>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Input.Select control={control} name="ids.company" label="Transportadora" placeholder="Todos" data={filtersData?.companies} />
            <Input.Select
              control={control}
              name="ids.driver"
              label="Motorista"
              placeholder="Todos"
              data={searchDriversResult.options}
              onSearchChange={setSearchDriver}
              onClear={() => setSearchDriver("")}
              searchValue={searchDriver}
              clearable
              leftSection={isFetchingDriver && <Loader size="xs" />}
            />
            <Input.Select
              control={control}
              name="ids.truck"
              label="Caminhão"
              placeholder="Todos"
              data={searchTrucksResult.options}
              onSearchChange={setSearchTruck}
              onClear={() => setSearchTruck("")}
              searchValue={searchTruck}
              clearable
              leftSection={isFetchingTruck && <Loader size="xs" />}
            />
            <Input.Select control={control} name="ids.order" label="Pedido" placeholder="Todos" data={filtersData?.orders} />
            {/*  <Input.Select
              control={control}
              name="ids.order"
              label="Pedido"
              placeholder="Todos"
              data={searchOrdersResult.options}
              onSearchChange={setSearchOrder}
              onClear={() => setSearchOrder("")}
              searchValue={searchOrder}
              clearable
              leftSection={isFetchingOrder && <Loader size="xs" />}
            /> */}
            <Input.Select
              control={control}
              name="ids.customer"
              label="Cliente"
              placeholder="Todos"
              data={searchCustomersResult.options}
              onSearchChange={setSearchCustomer}
              onClear={() => setSearchCustomer("")}
              searchValue={searchCustomer}
              clearable
              leftSection={isFetchingCustomer && <Loader size="xs" />}
            />
            <Input.Select control={control} name="groupBy" label="Agrupar por" placeholder="Todos" data={groupByOptions} />
          </div>

          <Divider my="xs" />

          <Stack>
            <Title order={4}>Período</Title>

            <Input.DateRange
              control={control}
              name="dateRange"
              maxDate={today.format(dateFormat)}
              size={isMobile ? "sm" : "md"}
              numberOfColumns={isMobile ? 1 : 2}
              presets={isMobile ? [] : datePresets}
              allowSingleDateInRange
            />
          </Stack>

          <Button color="red" type="submit" className="self-end" loading={isSubmitting || filtersLoading} fullWidth={isMobile}>
            Filtrar
          </Button>
        </Stack>
      </Card>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />
    </Stack>
  );
}
